/*
 * Сводка ресурсов группы над таблицами: кластеры по окружениям, модели в сервинге и с
 * чемпионом, здоровье приложений ArgoCD, активные алерты Prometheus (плитка открывает
 * попап с ресурсами) и список того, что требует внимания. Считается из уже загруженных
 * строк useResources, без дополнительных запросов.
 */
import { ReactNode, useState } from 'react';
import {
  ButtonBase,
  Grid,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { AsyncState } from 'react-use/esm/useAsync';
import { alertsByResource } from '../../lib/alerts';
import {
  CLUSTER_ENV_ANNOTATION,
  DEFAULT_CHAMPION_ALIAS,
  MLFLOW_CHAMPION_ALIAS_ANNOTATION,
  SERVING_ENVS,
  annotation,
  envRank,
} from '../../lib/viva';
import { ArgoApp, isHealthy } from '../argocd';
import { AlertsDialog } from './AlertsDialog';
import { Resources, deployRank } from './useResources';

const useStyles = makeStyles(theme => ({
  tile: {
    padding: theme.spacing(2, 2.5),
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    height: '100%',
    boxSizing: 'border-box',
  },
  // плитка-кнопка: та же вёрстка, но вся плитка кликабельна
  button: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    textAlign: 'left',
    width: '100%',
    height: '100%',
    borderRadius: 'inherit',
  },
  warning: {
    borderColor: theme.palette.status.warning,
    borderWidth: 1,
    borderStyle: 'solid',
  },
  valueWarning: { color: theme.palette.status.warning },
  alert: {
    borderColor: theme.palette.status.error,
    borderWidth: 1,
    borderStyle: 'solid',
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  value: {
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  valueAlert: { color: theme.palette.status.error },
  detail: { fontSize: 13, color: theme.palette.text.secondary },
  bar: {
    height: 8,
    borderRadius: 999,
    background: theme.palette.action.hover,
    overflow: 'hidden',
    marginTop: 6,
  },
  fill: {
    height: '100%',
    borderRadius: 999,
    background: theme.palette.status.ok,
  },
}));

const Tile = ({
  label,
  value,
  detail,
  alert,
  warning,
  onClick,
  children,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  alert?: boolean;
  /** Жёлтая рамка: есть что посмотреть, но не авария. */
  warning?: boolean;
  /** Плитка становится кнопкой (попап с подробностями). */
  onClick?: () => void;
  children?: ReactNode;
}) => {
  const classes = useStyles();
  const tones = { border: '', value: '' };
  if (alert) {
    tones.border = classes.alert;
    tones.value = classes.valueAlert;
  } else if (warning) {
    tones.border = classes.warning;
    tones.value = classes.valueWarning;
  }
  const { border: tone, value: valueTone } = tones;
  const content = (
    <>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={`${classes.value} ${valueTone}`}>
        {value}
      </Typography>
      {detail && <Typography className={classes.detail}>{detail}</Typography>}
      {children}
    </>
  );
  return (
    <Grid item xs={12} sm={6} md>
      {onClick ? (
        <Paper className={tone} style={{ height: '100%' }}>
          <ButtonBase
            className={`${classes.tile} ${classes.button}`}
            onClick={onClick}
            aria-haspopup="dialog"
          >
            {content}
          </ButtonBase>
        </Paper>
      ) : (
        <Paper className={`${classes.tile} ${tone}`}>{content}</Paper>
      )}
    </Grid>
  );
};

/** Проблема: приложение не Healthy или не Synced, с указанием кластера/модели. */
type Issue = { subject: string; app: ArgoApp };

const issuesOf = (subject: string, apps?: ArgoApp[]): Issue[] =>
  (apps ?? [])
    .filter(app => !isHealthy(app) || app.sync !== 'Synced')
    .map(app => ({ subject, app }));

const plural = (n: number, one: string, few: string, many: string) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

export const SummaryTiles = ({
  resources,
}: {
  resources: AsyncState<Resources>;
}) => {
  const classes = useStyles();
  const [alertsOpen, setAlertsOpen] = useState(false);
  const { value, loading } = resources;
  if (loading || !value) {
    return null;
  }
  const {
    clusters,
    models,
    services,
    databases,
    brokers,
    airflow,
    defectdojo,
  } = value;

  // Алерты Prometheus: кластеры со всеми алертами и ресурсы со своей долей
  const alertRows = alertsByResource(
    clusters.map(row => ({ entity: row.entity, alerts: row.alerts.value })),
    [
      ...databases.map(r => r.entity),
      ...brokers.map(r => r.entity),
      ...services.map(r => r.entity),
      ...models.map(r => r.entity),
      ...airflow,
      ...defectdojo,
    ],
  );
  const alertTotals = clusters.reduce(
    (acc, row) => {
      for (const a of row.alerts.value ?? []) {
        if (a.state === 'firing') acc.firing += 1;
        else acc.pending += 1;
      }
      return acc;
    },
    { firing: 0, pending: 0 },
  );
  const alertsUnavailable = clusters
    .filter(row => row.alerts.error)
    .map(row => row.entity.metadata.name);
  const alertTotal = alertTotals.firing + alertTotals.pending;
  let alertsDetail = `firing ${alertTotals.firing} · pending ${alertTotals.pending}`;
  if (alertTotal === 0) {
    alertsDetail = alertsUnavailable.length
      ? `Prometheus недоступен: ${alertsUnavailable.length}`
      : 'активных нет';
  }

  // Кластеры: количество по окружениям в порядке production → staging → остальные
  const envCounts = new Map<string, number>();
  clusters.forEach(({ entity }) => {
    const env = annotation(entity, CLUSTER_ENV_ANNOTATION) ?? 'без окружения';
    envCounts.set(env, (envCounts.get(env) ?? 0) + 1);
  });
  const envDetail = [...envCounts]
    .sort((a, b) => envRank(a[0]) - envRank(b[0]))
    .map(([env, n]) => `${env} ${n}`)
    .join(' · ');

  // Модели: в сервинге и с чемпионом
  const served = models.filter(
    row => deployRank(row) < SERVING_ENVS.length,
  ).length;
  const withChampion = models.filter(({ entity, summary }) => {
    const alias =
      annotation(entity, MLFLOW_CHAMPION_ALIAS_ANNOTATION) ??
      DEFAULT_CHAMPION_ALIAS;
    return Boolean(summary.value?.aliases[alias]);
  }).length;

  const servedServices = services.filter(
    row => (row.apps.value ?? []).length > 0,
  ).length;
  const prodServices = services.filter(row =>
    (row.apps.value ?? []).some(
      app => app.name === `${row.entity.metadata.name}-prod`,
    ),
  ).length;

  // Приложения ArgoCD платформы во всех кластерах
  const clusterApps = clusters.flatMap(row => row.apps.value ?? []);
  const healthy = clusterApps.filter(isHealthy).length;
  const ratio = clusterApps.length ? (healthy / clusterApps.length) * 100 : 0;

  // Требует внимания: проблемные приложения кластеров и моделей, недоступные источники
  const issues = [
    ...clusters.flatMap(row =>
      issuesOf(
        row.entity.metadata.title ?? row.entity.metadata.name,
        row.apps.value,
      ),
    ),
    ...models.flatMap(row =>
      issuesOf(row.entity.metadata.name, row.apps.value),
    ),
    ...services.flatMap(row =>
      issuesOf(row.entity.metadata.name, row.apps.value),
    ),
    ...[...databases, ...brokers].flatMap(row =>
      issuesOf(
        row.entity.metadata.title ?? row.entity.metadata.name,
        row.apps.value,
      ),
    ),
  ];
  const unavailable =
    clusters.filter(row => row.apps.error).length +
    models.filter(row => row.apps.error || row.summary.error).length +
    services.filter(row => row.apps.error).length +
    databases.filter(row => row.apps.error || row.databases.error).length +
    brokers.filter(row => row.apps.error || row.kafka.error).length;
  const attention = issues.length + unavailable;

  return (
    <Grid container spacing={2}>
      <Tile
        label="Кластеры"
        value={clusters.length}
        detail={envDetail || '—'}
      />
      <Tile
        label="Модели"
        value={models.length}
        detail={`в сервинге ${served} · с чемпионом ${withChampion}`}
      />
      <Tile
        label="Сервисы"
        value={services.length}
        detail={`развёрнуто ${servedServices} · в prod ${prodServices}`}
      />
      <Tile
        label="Приложения ArgoCD"
        value={
          clusterApps.length ? (
            <>
              {healthy}{' '}
              <Typography component="span" className={classes.detail}>
                / {clusterApps.length} Healthy
              </Typography>
            </>
          ) : (
            '—'
          )
        }
      >
        {clusterApps.length > 0 && (
          <div className={classes.bar}>
            <div className={classes.fill} style={{ width: `${ratio}%` }} />
          </div>
        )}
      </Tile>
      <Tile
        label="Алерты"
        value={alertTotal}
        alert={alertTotals.firing > 0}
        warning={alertTotals.firing === 0 && alertTotals.pending > 0}
        onClick={() => setAlertsOpen(true)}
        detail={alertsDetail}
      />
      <AlertsDialog
        open={alertsOpen}
        rows={alertRows}
        unavailable={alertsUnavailable}
        onClose={() => setAlertsOpen(false)}
      />
      <Tile
        label="Требует внимания"
        value={attention}
        alert={attention > 0}
        detail={
          attention === 0 ? (
            'всё в порядке'
          ) : (
            <>
              {issues.slice(0, 2).map(({ subject, app }) => (
                <span
                  key={`${subject}/${app.name}`}
                  style={{ display: 'block' }}
                >
                  {app.name} @ {subject} ·{' '}
                  {isHealthy(app) ? app.sync : app.health}
                </span>
              ))}
              {issues.length > 2 && (
                <span style={{ display: 'block' }}>
                  и ещё {issues.length - 2}{' '}
                  {plural(issues.length - 2, 'проблема', 'проблемы', 'проблем')}
                </span>
              )}
              {unavailable > 0 && (
                <span style={{ display: 'block' }}>
                  источники недоступны: {unavailable}
                </span>
              )}
            </>
          )
        }
      />
    </Grid>
  );
};
